/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import PropTypes from 'prop-types';
import { useTable, usePagination } from 'react-table';
import Cell from '../../Users/components/UsersTable/CellTypes/Cell';
import LinkCell from '../../Users/components/UsersTable/CellTypes/LinkCell';
import DateCell from '../../Users/components/UsersTable/CellTypes/DateCell';
import CurrencyCell from '../../Users/components/UsersTable/CellTypes/CurrencyCell';
import styles from './InvoicesTable.module.less';

const defaultColumn = {
  Cell,
  LinkCell,
  DateCell,
  CurrencyCell,
};

const invoicesTable = ({ columns, data, tableProps, paginationObject, fetchInvoices, currentPage, ...props }) => {
  const totalPages = Math.ceil(paginationObject.count / 10);
  const nextPageUrl = paginationObject.next;
  const previousUrl = paginationObject.previous;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: {},
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageSize: 10,
        manualPagination: true,
      },
    },
    usePagination,
  );

  const pageNumbers = () => {
    const TOTAL_SHOW = 5;
    const middle = parseInt(TOTAL_SHOW / 2, 10);
    const startIndex =
      totalPages > TOTAL_SHOW
        ? Math.max(1, currentPage - middle) -
          (totalPages - currentPage < middle ? middle - (totalPages - currentPage) : 0)
        : 1;
    const endIndex = Math.min(totalPages, startIndex + TOTAL_SHOW);

    const numbers = [];
    for (let i = startIndex; i <= endIndex; i++) {
      numbers.push(i);
    }

    const startRange = startIndex - 1;

    return (
      <>
        {startRange >= middle - 1 && (
          <span
            tabIndex={0}
            role="button"
            onClick={() => props.setPage(startRange)}
            className={startRange === currentPage ? styles.selectedPageNumber : styles.additionalPagesNumber}
            key={startRange}
          >
            ...
          </span>
        )}

        {numbers.map((pageNumber) => (
          <span
            tabIndex={0}
            role="button"
            onClick={() => props.setPage(pageNumber)}
            className={pageNumber === currentPage ? styles.selectedPageNumber : styles.additionalPagesNumber}
            key={pageNumber}
          >
            {totalPages > TOTAL_SHOW &&
            pageNumber === endIndex &&
            (totalPages - middle > currentPage || pageNumber !== totalPages)
              ? '...'
              : pageNumber}
          </span>
        ))}
      </>
    );
  };

  function getButtonStyle(canPrevNext) {
    const buttonStyle = [styles.paginationButton];

    if (!canPrevNext) {
      buttonStyle.push(styles.disabledButton);
    }

    return buttonStyle.join(' ');
  }

  function getCellStyle(cell) {
    if (cell.column.Header === 'Balance') {
      if (cell.value !== 0) {
        return styles.balanceRed;
      }
      return styles.balanceGreen;
    }
    if (cell.column.Header === 'Issue Date') {
      return styles.alignLeft;
    }
    if (cell.column.Header === 'Product') {
      return styles.productCell;
    }
    if (cell.column.Header === 'Paid') {
      return styles.paidCell;
    }
    return '';
  }

  function getCellValue(cell) {
    const balance = cell.row.values.balance.toFixed(2);

    let value = cell.render('Cell', { tableProps });

    if (cell.column.Header === 'Balance') {
      value = `$${balance.toString()}`;
    }
    if (cell.column.Header === 'Invoice') {
      value = cell.render('LinkCell', { tableProps, linkAccessor: 'invoice_link' });
    }

    if (cell.column.Header === 'Issue Date') {
      value = cell.render('DateCell', { tableProps });
    }

    if (cell.column.Header === 'Amount' || cell.column.Header === 'Paid') {
      value = cell.render('CurrencyCell', { tableProps });
    }
    return value;
  }

  function getColumnClass(column) {
    if (column.Header === 'Issue Date') {
      return column.Header.replace(' ', '');
    }
    const header = column.Header.toLowerCase();
    return [styles.column, styles[header]].join(' ');
  }

  return (
    <div>
      <div className={styles.header}>{gettext('Invoices')}</div>
      <div className={styles.tableContainer}>
        <table className={styles.invoicesTable} {...getTableProps()}>
          <thead>
            {headerGroups.map((group) => (
              <tr {...group.getHeaderGroupProps()}>
                {group.headers.map((column) => {
                  return (
                    <th className={getColumnClass(column)} {...column.getHeaderProps()}>
                      {column.render('Header')}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {!props.isLoading && data.length > 0 && (
            <tbody {...getTableBodyProps()}>
              {!props.isLoading &&
                page.map((row, i) => {
                  prepareRow(row);

                  return (
                    <tr className={styles.column} {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td className={getCellStyle(cell)} {...cell.getCellProps()}>
                            {getCellValue(cell)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          )}
          <tfoot />
        </table>
      </div>
      {!props.isLoading && data.length === 0 && (
        <label className={styles.emptyTableText}>{gettext('Oops… No Invoices found.')}</label>
      )}
      {props.isLoading && (
        <div style={{ fontSize: 20, lineHeight: 2, width: '100%', marginTop: 20 }}>
          <Skeleton count={10} />
        </div>
      )}
      {data.length > 0 ? (
        <div className={styles.paginationContainer}>
          <button
            type="button"
            className={getButtonStyle(previousUrl)}
            onClick={() => props.previousPage()}
            disabled={!previousUrl}
          >
            {'<< '}
            {gettext('Previous')}
          </button>{' '}
          <div className={styles.pageNumbersContainer}>{pageNumbers()}</div>
          <button
            type="button"
            className={getButtonStyle(nextPageUrl)}
            onClick={() => props.nextPage()}
            disabled={!nextPageUrl}
          >
            {gettext('Next')}
            {' >>'}
          </button>{' '}
        </div>
      ) : null}
    </div>
  );
};

invoicesTable.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  columns: PropTypes.array,
  tableProps: PropTypes.object,
  paginationObject: PropTypes.object,
  fetchInvoices: PropTypes.func,

  previousPage: PropTypes.func,
  nextPage: PropTypes.func,
  setPage: PropTypes.func,
  currentPage: PropTypes.number,
};

export default invoicesTable;
